import { useState, type FormEvent } from "react";
import Reveal from "@/components/feature/Reveal";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dai37g3p14a1h3mr6vog";

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("company_alt") as string | null || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("Thank you for contacting RJB4 Restaurant Group. We'll be in touch soon.");
      return;
    }
    formData.delete("company_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("loading");

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const ok = res.ok && parsed?.code === "OK";
      if (ok) {
        setStatus("success");
        setFeedback("Thank you for contacting RJB4 Restaurant Group. We'll be in touch soon.");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(
          parsed?.meta?.message || parsed?.message || "Something went wrong. Please try again."
        );
      }
    } catch {
      setStatus("error");
      setFeedback("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-primary-600 font-medium">
            Contact
          </p>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight tracking-tight">
            Let&rsquo;s Connect.
          </h2>
          <p className="mt-5 text-foreground-600 text-base md:text-lg font-light leading-relaxed">
            For business inquiries, partnerships, or general information, please get in touch
            with RJB4 Restaurant Group, LLC.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <Reveal>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-8 md:p-10 h-full">
              <h3 className="font-heading text-2xl text-foreground-950">
                Contact Information
              </h3>

              <div className="mt-8 space-y-7">
                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-primary-700">
                    <i className="ri-user-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-foreground-400">
                      Owner
                    </p>
                    <p className="mt-1 text-foreground-900 font-medium">[FULL NAME]</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-primary-700">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-foreground-400">
                      Email
                    </p>
                    <a
                      href="mailto:russell@rjb4restaurant.com"
                      className="mt-1 block text-foreground-900 font-medium hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      russell@rjb4restaurant.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-primary-700">
                    <i className="ri-phone-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-foreground-400">
                      Phone
                    </p>
                    <a
                      href="tel:+19493703217"
                      className="mt-1 block text-foreground-900 font-medium hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      (949) 370-3217
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-primary-700">
                    <i className="ri-map-pin-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-foreground-400">
                      Address
                    </p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=2+81+Via+Guadix%2C+Mission+Viejo%2C+CA+92691"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="mt-1 block text-foreground-900 font-medium hover:text-primary-600 transition-colors leading-relaxed cursor-pointer"
                    >
                      2 81 Via Guadix
                      <br />
                      Mission Viejo, CA 92691
                      <br />
                      United States
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="contact-form bg-background-50 border border-background-200/70 rounded-lg p-8 md:p-10"
            >
              <h3 className="font-heading text-2xl text-foreground-950">Send a Message</h3>

              <div className="mt-8 space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    className="w-full px-4 py-3 rounded-md border border-background-300/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    className="w-full px-4 py-3 rounded-md border border-background-300/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    className="w-full px-4 py-3 rounded-md border border-background-300/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
                    placeholder="(949) 000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-md border border-background-300/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition resize-none"
                    placeholder="How can we help?"
                  />
                </div>

                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {feedback && (
                <div
                  className={`mt-5 px-4 py-3 rounded-md text-sm ${
                    status === "success"
                      ? "bg-primary-100 text-primary-900"
                      : "bg-secondary-100 text-secondary-900"
                  }`}
                >
                  {feedback}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm md:text-base font-medium px-8 py-3.5 rounded-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}